import { fetchOnboardingDetails } from '@common/onboarding/query';
import { OnboardingStatus } from '@common/onboarding/types';
import { captureException } from '@common/sentry';
import { mdClient } from '@LSP/websocket/MdClient/MdWebSocketBase';
import { GetUserOnboardingDetails } from '@pages/onboarding/query';
import { getReplayDetailsUrl } from '@pages/Sql/Replay/Share/replayDetails';
import { getShareDetailsUrl } from '@pages/Sql/Replay/Share/shareDetails';
import { noop } from 'lodash-es';

import {
  getAppSession,
  getAthenaTrial,
  getGlobalConf,
  getKeepAliveSession,
  getUnSecureGlobalConfig,
  getUser,
  getUserFabricStatus
} from '../data/apis/api';
import { fetchUpdateInfo } from '../data/apis/athena';
import { makeGraphQlCall } from '../data/util';
import { isAnyWebsocketAlive } from '../data/websocket-counter';
import { CSRF_TOKEN_KEY, csrf } from '../utils/csrf';
import { WORKFLOW_DEBUG_MODE_KEY } from '../utils/localstorage-keys';
import { GlobalWindow } from '../utils/types';
import { DefaultEditionConfig, fetchEditionConfig } from './edition';
import { DefaultEnabledTranspilers, fetchEnabledTranspilers } from './enabledTranspilers';
import { FEATURE_MANAGEMENT_ENABLED_KEY } from './globalConfig/securedConfigHook';
import { TrialInfo, AppMetadataProviderType, UserDetails, UserFabricStatusKnownStatus } from './types';

let global = window as unknown as GlobalWindow;
global.__current_user_info = {
  id: ''
};
export const getRecentUpdateInfo = async () => {
  try {
    const maintenanceUpdateInfoData = await fetchUpdateInfo();
    if (maintenanceUpdateInfoData?.success) {
      return maintenanceUpdateInfoData?.data;
    }
  } catch (error: unknown) {
    console.error(error);
    // To avoid the error case when we load userinfo
    return {};
  }
};

async function fetchUserDetails(execURl: string): Promise<UserDetails> {
  // merge fabric status from execution API
  const [userDetailsResult, userFabricStatusResult] = await Promise.allSettled([
    getUser<Omit<UserDetails, 'fabric'>>(),
    getUserFabricStatus(execURl)
  ]);
  let userDetails: UserDetails;

  if (userDetailsResult.status === 'fulfilled') userDetails = userDetailsResult.value;
  else throw userDetailsResult.reason;
  // if fabric status api fails don't throw and set the fabric status to -1
  userDetails.fabric =
    userFabricStatusResult.status === 'fulfilled'
      ? userFabricStatusResult.value.fabric
      : UserFabricStatusKnownStatus.AlreadyHaveFabric;

  return userDetails;
}
type ConfigType = { [key: string]: boolean | undefined | string };
const fetchUnsecuredGlobalConfig = async () => {
  try {
    const resp = (await getUnSecureGlobalConfig()) as unknown as ConfigType[];
    let settings: ConfigType = {};
    (resp || []).forEach((config) => {
      settings = { ...settings, ...config };
    });
    return settings;
  } catch (error) {
    console.error(error);
    return {};
  }
};
function keepTokenAlive() {
  const schedule = () => {
    setTimeout(
      () => {
        if (!isAnyWebsocketAlive() && document.hidden) {
          // document.hidden will be true if tab is not visible
          return;
        }

        getKeepAliveSession()
          .then(schedule) // schedule next one only when the API succeeds. It will fail in case of error.
          .catch(noop);
      },
      3 * 60 * 1000
    ); // 3 minutes
  };

  document.addEventListener('visibilitychange', function () {
    // we need to refresh token when ever there visibility change visible -> hidden or hidden to active.
    // visible -> hidden is required so that we make sure, token is not expired just after the tab change
    // hidden -> visible is required so that we can check if token is valid
    getKeepAliveSession().catch(noop);
  });

  schedule();
}

const fetchSecuredGlobalConfig = async () => {
  try {
    const resp = (await getGlobalConf('/globalConf')) as unknown as ConfigType[];
    let settings: ConfigType = {};
    (resp || []).forEach((config) => {
      settings = { ...settings, ...config };
    });
    return settings;
  } catch (error) {
    console.error(error);
    return {};
  }
};
export async function getAppMetadata(shareId: string | null, replayId: string | null) {
  const appMetadata: AppMetadataProviderType = {
    user: {
      id: '',
      email: ''
    },
    globalConfig: {
      onboardingStatus: OnboardingStatus.NOT_SET,
      sqlOnboardingStatus: OnboardingStatus.NOT_SET,
      unsetFabricStatusId: -1,
      isFabricSet: false
    },
    unsecuredConfig: {},
    cookieAccepted: false,
    maintenanceUpdateInfo: {},
    setMaintenanceUpdateInfo: () => null,
    editionConfig: DefaultEditionConfig,
    enabledTranspilers: DefaultEnabledTranspilers,
    shareUrl: null,
    replayUrl: null
  };

  appMetadata.setMaintenanceUpdateInfo = async () => {
    appMetadata.maintenanceUpdateInfo = await getRecentUpdateInfo();
  };

  let appMetadataPromises = [];

  try {
    // fetch csrf
    const session = await getAppSession<{ [CSRF_TOKEN_KEY]: string; 'accept-prophecy-cookies': boolean }>();
    const token = session[CSRF_TOKEN_KEY];
    appMetadata.cookieAccepted = session['accept-prophecy-cookies'];
    const unsecuredConfigPromise = fetchUnsecuredGlobalConfig().then((config) => {
      appMetadata.unsecuredConfig = config;
    });

    appMetadataPromises.push(unsecuredConfigPromise);

    if (token) {
      csrf.set(token);
    } else {
      await Promise.allSettled(appMetadataPromises);
      return appMetadata;
    }

    //Check if user trying to login using token
    if (window.location.href.indexOf('loginUsingToken') >= 0) {
      await Promise.allSettled(appMetadataPromises);
      return appMetadata;
    }

    let shareOreReplayUrlPromise = Promise.resolve();
    if (shareId) {
      shareOreReplayUrlPromise = getShareDetailsUrl(shareId).then((data) => {
        appMetadata.shareUrl = data?.url ?? null;
      });
    }
    if (replayId) {
      shareOreReplayUrlPromise = getReplayDetailsUrl(replayId).then((data) => {
        appMetadata.replayUrl = data?.url ?? null;
      });
    }
    appMetadataPromises.push(shareOreReplayUrlPromise);

    // for global config, we do need share id so we will have to wait for it.
    await shareOreReplayUrlPromise;

    const globalConfigPromise = fetchSecuredGlobalConfig().then((config) => {
      appMetadata.globalConfig = { ...appMetadata.globalConfig, ...config };
    });
    appMetadataPromises.push(globalConfigPromise);

    const maintenanceUpdateInfoPromise = getRecentUpdateInfo().then((info) => {
      appMetadata.maintenanceUpdateInfo = info;
    });
    appMetadataPromises.push(maintenanceUpdateInfoPromise);

    const editionConfigPromise = fetchEditionConfig().then((config) => {
      appMetadata.editionConfig = config;
    });
    appMetadataPromises.push(editionConfigPromise);

    // wait for global config before calling further apis
    await globalConfigPromise;

    const isFeatureManagementAvailable = appMetadata.globalConfig[FEATURE_MANAGEMENT_ENABLED_KEY];
    if (isFeatureManagementAvailable) {
      const enabledTranspilersPromise = fetchEnabledTranspilers().then((transpilers) => {
        appMetadata.enabledTranspilers = transpilers;
      });
      appMetadataPromises.push(enabledTranspilersPromise);
    }
    const userDataPromise = fetchUserDetails(appMetadata.globalConfig?.['prophecy.ui.execution.url'] as string);

    // for other details, which are dependent on the user details wait for it.
    const userData = await userDataPromise;

    if (!userData?.id) {
      await Promise.allSettled(appMetadataPromises);
      return appMetadata;
    }

    // store email and id on global container
    global.__current_user_info.id = userData.id;

    appMetadata.user = {
      ...appMetadata.user,
      ...userData,
      fabric: Number(userData.fabric)
    };

    let onboardingDetail: UserDetails['onboardingDetail'];
    let userOnboardingDetails: UserDetails['userOnboardingDetails'];

    const onboardingPromise = Promise.all([
      fetchOnboardingDetails(
        { uid: userData.id },
        Boolean(appMetadata.globalConfig['prophecy.ui.backend.sql.enabled']),
        Boolean(appMetadata.globalConfig['prophecy.ui.airflow.enabled'])
      ),
      makeGraphQlCall(GetUserOnboardingDetails, { uid: userData.id })
    ])
      .then(([onboardingDetailResult, userOnboardingDetailsResult]) => {
        onboardingDetail = onboardingDetailResult;
        userOnboardingDetails = userOnboardingDetailsResult?.User;

        if (onboardingDetail) {
          appMetadata.globalConfig.onboardingStatus = onboardingDetail.lastFlowCompleted as OnboardingStatus;
          appMetadata.globalConfig.sqlOnboardingStatus = onboardingDetail.lastFlowCompletedSql as OnboardingStatus;
        }

        appMetadata.user!.onboardingDetail = onboardingDetail;
        appMetadata.user!.userOnboardingDetails = userOnboardingDetails;
      })
      .catch((error) => {
        // ignore error as it is already handled in fetchOnboardingDetails
      });
    appMetadataPromises.push(onboardingPromise);

    // turn off debug mode by default for user with prophecy email
    if (userData.email.endsWith('@prophecy.io')) {
      const wasSet = localStorage.getItem(WORKFLOW_DEBUG_MODE_KEY);
      if (!wasSet) {
        localStorage.setItem(WORKFLOW_DEBUG_MODE_KEY, 'false');
      }
    }

    // wait for unsecured config to be set before checking if metering is enabled
    await unsecuredConfigPromise;
    if (appMetadata.unsecuredConfig?.['prophecy.ui.unsecured.metering.enabled'] && userData.email) {
      const trialInfoPromise = getAthenaTrial<TrialInfo>()
        .then((trialInfo) => {
          appMetadata.user!.trialInfo = trialInfo;
        })
        .catch((error) => {
          captureException({ exception: error });
        });

      appMetadataPromises.push(trialInfoPromise);
    }

    mdClient.lazyInit();

    keepTokenAlive();
    // wait for all promises to be settled
    await Promise.allSettled(appMetadataPromises);
    return appMetadata;
  } catch (error) {
    //wait for all promises to be resolved
    await Promise.allSettled(appMetadataPromises);
    return appMetadata;
  }
}
